import { useMemo, useState, useCallback, useRef } from "react";
import { scaleLinear, scaleOrdinal } from "@visx/scale";
import { Group } from "@visx/group";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridRows, GridColumns } from "@visx/grid";
import { ParentSize } from "@visx/responsive";
import { useTooltip } from "@visx/tooltip";
import { localPoint } from "@visx/event";
import { ChartTooltip } from "../../../shared/components/ChartTooltip/ChartTooltip";
import { ChartLegend } from "../../../shared/components/ChartLegend/ChartLegend";
import type { TopicClusterResponse } from "../../types";
import { formatNumber, formatShortDate } from "../../utils/formatters";
import styles from "./TopicClusterChart.module.css";

type ThreadPoint = TopicClusterResponse["threads"][number];

const MARGIN = { top: 16, right: 16, bottom: 40, left: 48 };
const POINT_RADIUS = 5;
const POINT_RADIUS_HOVER = 7;
const MIN_SCALE = 0.5;
const MAX_SCALE = 10;

const CLUSTER_COLORS = [
  "#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6",
  "#ec4899", "#14b8a6", "#f97316", "#06b6d4", "#84cc16",
  "#e879f9", "#22d3ee", "#fb923c", "#a78bfa", "#34d399",
  "#fbbf24", "#f472b6", "#2dd4bf", "#818cf8", "#a3e635",
];
const NOISE_COLOR = "#d1d5db";

function getClusterColor(clusterId: number): string {
  if (clusterId < 0) return NOISE_COLOR;
  return CLUSTER_COLORS[clusterId % CLUSTER_COLORS.length];
}

interface ZoomState {
  scale: number;
  translateX: number;
  translateY: number;
}

const INITIAL_ZOOM: ZoomState = { scale: 1, translateX: 0, translateY: 0 };

function clampScale(s: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
}

interface InnerChartProps {
  data: TopicClusterResponse;
  width: number;
  height: number;
}

function InnerChart({ data, width, height }: InnerChartProps) {
  const [selectedPoint, setSelectedPoint] = useState<ThreadPoint | null>(null);
  const [zoom, setZoom] = useState<ZoomState>(INITIAL_ZOOM);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  const {
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    showTooltip,
    hideTooltip,
  } = useTooltip<ThreadPoint>();

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  const { xScale, yScale } = useMemo(() => {
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    for (const t of data.threads) {
      if (t.x < xMin) xMin = t.x;
      if (t.x > xMax) xMax = t.x;
      if (t.y < yMin) yMin = t.y;
      if (t.y > yMax) yMax = t.y;
    }
    const xPad = (xMax - xMin) * 0.05 || 1;
    const yPad = (yMax - yMin) * 0.05 || 1;

    return {
      xScale: scaleLinear({
        domain: [xMin - xPad, xMax + xPad],
        range: [0, innerWidth],
      }),
      yScale: scaleLinear({
        domain: [yMin - yPad, yMax + yPad],
        range: [innerHeight, 0],
      }),
    };
  }, [data.threads, innerWidth, innerHeight]);

  const clusters = useMemo(() => {
    return [...data.cluster_summary].sort(
      (a, b) => b.thread_count - a.thread_count,
    );
  }, [data.cluster_summary]);

  const colorScale = useMemo(
    () =>
      scaleOrdinal({
        domain: clusters.map((c) => String(c.cluster_id)),
        range: clusters.map((c) => getClusterColor(c.cluster_id)),
      }),
    [clusters],
  );

  const clusterBounds = useMemo(() => {
    const groups = new Map<
      number,
      { sumX: number; sumY: number; points: { px: number; py: number }[] }
    >();
    for (const t of data.threads) {
      if (t.cluster_id < 0) continue;
      const px = xScale(t.x);
      const py = yScale(t.y);
      let g = groups.get(t.cluster_id);
      if (!g) {
        g = { sumX: 0, sumY: 0, points: [] };
        groups.set(t.cluster_id, g);
      }
      g.sumX += px;
      g.sumY += py;
      g.points.push({ px, py });
    }
    const result: {
      clusterId: number;
      cx: number;
      cy: number;
      r: number;
      color: string;
    }[] = [];
    for (const [clusterId, g] of groups) {
      if (g.points.length < 2) continue;
      const cx = g.sumX / g.points.length;
      const cy = g.sumY / g.points.length;
      let maxDist = 0;
      for (const p of g.points) {
        const d = Math.sqrt((p.px - cx) ** 2 + (p.py - cy) ** 2);
        if (d > maxDist) maxDist = d;
      }
      result.push({
        clusterId,
        cx,
        cy,
        r: maxDist + POINT_RADIUS + 6,
        color: getClusterColor(clusterId),
      });
    }
    return result;
  }, [data.threads, xScale, yScale]);

  const legendItems = useMemo(
    () =>
      clusters
        .filter((c) => c.cluster_id >= 0)
        .slice(0, 10)
        .map((c) => ({
          label: c.label ?? `Cluster ${c.cluster_id}`,
          color: getClusterColor(c.cluster_id),
          value: c.thread_count,
        })),
    [clusters],
  );

  const handlePointHover = useCallback(
    (event: React.MouseEvent<SVGCircleElement>, point: ThreadPoint) => {
      const svgPoint = localPoint(event);
      if (!svgPoint) return;
      showTooltip({
        tooltipData: point,
        tooltipLeft: svgPoint.x,
        tooltipTop: svgPoint.y,
      });
    },
    [showTooltip],
  );

  const handlePointClick = useCallback((point: ThreadPoint) => {
    setSelectedPoint((prev) =>
      prev?.thread_id === point.thread_id ? null : point,
    );
  }, []);

  // Zoom via wheel
  const handleWheel = useCallback(
    (event: React.WheelEvent<SVGSVGElement>) => {
      event.preventDefault();
      const svgPoint = localPoint(event);
      if (!svgPoint) return;

      const factor = event.deltaY > 0 ? 0.9 : 1.1;
      setZoom((prev) => {
        const newScale = clampScale(prev.scale * factor);
        const ratio = newScale / prev.scale;
        // Zoom toward the cursor position
        const newTx = svgPoint.x - ratio * (svgPoint.x - prev.translateX);
        const newTy = svgPoint.y - ratio * (svgPoint.y - prev.translateY);
        return { scale: newScale, translateX: newTx, translateY: newTy };
      });
    },
    [],
  );

  // Pan via drag
  const handleMouseDown = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      // Only start drag on primary button
      if (event.button !== 0) return;
      setIsDragging(true);
      dragStart.current = {
        x: event.clientX,
        y: event.clientY,
        tx: zoom.translateX,
        ty: zoom.translateY,
      };
    },
    [zoom.translateX, zoom.translateY],
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      if (!isDragging || !dragStart.current) return;
      const dx = event.clientX - dragStart.current.x;
      const dy = event.clientY - dragStart.current.y;
      setZoom((prev) => ({
        ...prev,
        translateX: dragStart.current!.tx + dx,
        translateY: dragStart.current!.ty + dy,
      }));
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    dragStart.current = null;
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
    dragStart.current = null;
    hideTooltip();
  }, [hideTooltip]);

  const zoomIn = useCallback(() => {
    setZoom((prev) => {
      const newScale = clampScale(prev.scale * 1.3);
      const ratio = newScale / prev.scale;
      const cx = width / 2;
      const cy = height / 2;
      return {
        scale: newScale,
        translateX: cx - ratio * (cx - prev.translateX),
        translateY: cy - ratio * (cy - prev.translateY),
      };
    });
  }, [width, height]);

  const zoomOut = useCallback(() => {
    setZoom((prev) => {
      const newScale = clampScale(prev.scale * 0.7);
      const ratio = newScale / prev.scale;
      const cx = width / 2;
      const cy = height / 2;
      return {
        scale: newScale,
        translateX: cx - ratio * (cx - prev.translateX),
        translateY: cy - ratio * (cy - prev.translateY),
      };
    });
  }, [width, height]);

  const resetZoom = useCallback(() => setZoom(INITIAL_ZOOM), []);

  if (innerWidth <= 0 || innerHeight <= 0) return null;

  const transform = `translate(${zoom.translateX}, ${zoom.translateY}) scale(${zoom.scale})`;

  return (
    <div className={styles.chartWrapper}>
      <div className={styles.controls}>
        <div className={styles.stats}>
          <span>
            Threads: <span className={styles.statValue}>{formatNumber(data.total_threads)}</span>
          </span>
          <span>
            Clusters: <span className={styles.statValue}>{formatNumber(data.total_clusters)}</span>
          </span>
        </div>
      </div>

      <ChartLegend items={legendItems} />

      <div style={{ position: "relative" }}>
        <svg
          width={width}
          height={height}
          role="img"
          aria-label="Topic cluster scatter plot"
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
          onWheel={handleWheel}
        >
          {/* Invisible overlay for drag */}
          <rect
            width={width}
            height={height}
            fill="transparent"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          />
          <g transform={transform}>
            <Group left={MARGIN.left} top={MARGIN.top}>
              <GridRows
                scale={yScale}
                width={innerWidth}
                stroke="var(--colours-neutral-100, #f3f4f6)"
              />
              <GridColumns
                scale={xScale}
                height={innerHeight}
                stroke="var(--colours-neutral-100, #f3f4f6)"
              />
              <AxisBottom
                top={innerHeight}
                scale={xScale}
                numTicks={6}
                stroke="var(--colours-neutral-200, #e5e7eb)"
                tickStroke="var(--colours-neutral-200, #e5e7eb)"
                tickLabelProps={() => ({
                  fill: "var(--colours-neutral-500, #6b7280)",
                  fontSize: 11,
                  textAnchor: "middle" as const,
                })}
              />
              <AxisLeft
                scale={yScale}
                numTicks={5}
                stroke="var(--colours-neutral-200, #e5e7eb)"
                tickStroke="var(--colours-neutral-200, #e5e7eb)"
                tickLabelProps={() => ({
                  fill: "var(--colours-neutral-500, #6b7280)",
                  fontSize: 11,
                  textAnchor: "end" as const,
                  dx: -4,
                  dy: 4,
                })}
              />
              {/* Cluster boundary circles */}
              {clusterBounds.map((b) => (
                <circle
                  key={`bound-${b.clusterId}`}
                  cx={b.cx}
                  cy={b.cy}
                  r={b.r}
                  fill={b.color}
                  fillOpacity={0.08}
                  stroke={b.color}
                  strokeOpacity={0.2}
                  strokeWidth={1}
                  pointerEvents="none"
                />
              ))}
              {/* Noise points rendered first (behind cluster points) */}
              {data.threads
                .filter((t) => t.cluster_id < 0)
                .map((point) => (
                  <circle
                    key={point.thread_id}
                    cx={xScale(point.x)}
                    cy={yScale(point.y)}
                    r={
                      selectedPoint?.thread_id === point.thread_id
                        ? POINT_RADIUS_HOVER
                        : POINT_RADIUS
                    }
                    fill={NOISE_COLOR}
                    fillOpacity={0.4}
                    stroke={
                      selectedPoint?.thread_id === point.thread_id
                        ? "#374151"
                        : "none"
                    }
                    strokeWidth={selectedPoint?.thread_id === point.thread_id ? 2 : 0}
                    cursor="pointer"
                    onMouseEnter={(e) => handlePointHover(e, point)}
                    onMouseLeave={hideTooltip}
                    onClick={() => handlePointClick(point)}
                  />
                ))}
              {/* Cluster points */}
              {data.threads
                .filter((t) => t.cluster_id >= 0)
                .map((point) => (
                  <circle
                    key={point.thread_id}
                    cx={xScale(point.x)}
                    cy={yScale(point.y)}
                    r={
                      selectedPoint?.thread_id === point.thread_id
                        ? POINT_RADIUS_HOVER
                        : POINT_RADIUS
                    }
                    fill={colorScale(String(point.cluster_id))}
                    fillOpacity={0.7}
                    stroke={
                      selectedPoint?.thread_id === point.thread_id
                        ? "#374151"
                        : getClusterColor(point.cluster_id)
                    }
                    strokeWidth={selectedPoint?.thread_id === point.thread_id ? 2 : 1}
                    strokeOpacity={selectedPoint?.thread_id === point.thread_id ? 1 : 0.3}
                    cursor="pointer"
                    onMouseEnter={(e) => handlePointHover(e, point)}
                    onMouseLeave={hideTooltip}
                    onClick={() => handlePointClick(point)}
                  />
                ))}
            </Group>
          </g>
        </svg>

        {/* Zoom controls */}
        <div className={styles.zoomControls} style={{ position: "absolute", bottom: 8, right: 8 }}>
          <button
            type="button"
            className={styles.zoomBtn}
            onClick={zoomIn}
            aria-label="Zoom in"
          >
            +
          </button>
          <button
            type="button"
            className={styles.zoomBtn}
            onClick={zoomOut}
            aria-label="Zoom out"
          >
            &minus;
          </button>
          <button
            type="button"
            className={styles.zoomBtn}
            onClick={resetZoom}
            aria-label="Reset zoom"
          >
            &#x21ba;
          </button>
        </div>
      </div>

      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <ChartTooltip top={tooltipTop ?? 0} left={tooltipLeft ?? 0}>
          <div className={styles.tooltipContent}>
            <strong>
              {tooltipData.title ?? tooltipData.question.slice(0, 60)}
              {!tooltipData.title && tooltipData.question.length > 60 ? "\u2026" : ""}
            </strong>
            <span className={styles.tooltipCluster}>
              {tooltipData.cluster_id < 0
                ? "Noise"
                : tooltipData.cluster_label ?? `Cluster ${tooltipData.cluster_id}`}
            </span>
          </div>
        </ChartTooltip>
      )}

      {/* Inspect panel */}
      {selectedPoint && (
        <div className={styles.inspectPanel}>
          <div className={styles.inspectHeader}>
            <span className={styles.inspectTitle}>
              {selectedPoint.title ?? "Untitled Thread"}
            </span>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => setSelectedPoint(null)}
              aria-label="Close inspect panel"
            >
              &#x2715;
            </button>
          </div>
          <div className={styles.inspectField}>
            <span className={styles.inspectLabel}>Cluster </span>
            {selectedPoint.cluster_id < 0
              ? "Noise"
              : selectedPoint.cluster_label ?? `Cluster ${selectedPoint.cluster_id}`}
          </div>
          <div className={styles.inspectField}>
            <span className={styles.inspectLabel}>Created </span>
            {formatShortDate(selectedPoint.created_at.slice(0, 10))}
          </div>
          <div className={styles.inspectField}>
            <span className={styles.inspectLabel}>Thread </span>
            {selectedPoint.thread_id.slice(0, 12)}&hellip;
          </div>
          <div className={styles.inspectQuestion}>
            {selectedPoint.question}
          </div>
        </div>
      )}
    </div>
  );
}

export interface TopicClusterChartProps {
  data: TopicClusterResponse;
}

const CHART_HEIGHT = 420;

export function TopicClusterChart({ data }: TopicClusterChartProps) {
  if (data.threads.length === 0) {
    return (
      <div className={styles.emptyState}>
        No topic cluster data for this period.
      </div>
    );
  }

  return (
    <ParentSize>
      {({ width }) => (
        <InnerChart data={data} width={width} height={CHART_HEIGHT} />
      )}
    </ParentSize>
  );
}
